import {
  ParsedTrackedVehicle,
  parseTrackedVehicleResponse,
} from '@/api/requests/oram/utils/oramDataParser';
import {
  RequestInitProps,
  RawTrackedVehicle,
  CreateTrackedVehicleParams,
  FetchTrackedVehicleParams,
  UpdateTrackedVehicleDetailParams,
  UpdateTrackedVehicleParams,
  CreateServiceItemParams,
  CompleteServiceItemParams,
  CreateRegistrationParams,
  RawServiceItem,
  CompletedServiceItem,
  CreateRegistrationResponse,
} from '@/myGarage/oramInterfaces';
import { constructURL } from '@/utils/index';
import { request } from '@/utils/requestHelpers';

export interface OramRequestsInterface {
  fetchTrackedVehicle: (
    fetchTrackedVehicleParams: FetchTrackedVehicleParams,
    token: string,
    queryKey: string
  ) => Promise<ParsedTrackedVehicle>;

  createTrackedVehicle: (
    token: string,
    params: CreateTrackedVehicleParams
  ) => Promise<ParsedTrackedVehicle>;

  updateTrackedVehicle: (
    token: string,
    updateTrackedVehicleParams: UpdateTrackedVehicleParams
  ) => Promise<ParsedTrackedVehicle>;

  updateVehicleDetail: (
    token: string,
    vehiclePermalink: string,
    newFormData: UpdateTrackedVehicleDetailParams,
    currentOramData: UpdateTrackedVehicleDetailParams
  ) => Promise<{ vehiclePermalink: string }>;

  createServiceItem: (
    token: string,
    createParams: CreateServiceItemParams
  ) => Promise<RawServiceItem>;

  completeServiceItem: (
    token: string,
    completeServiceItemParams: CompleteServiceItemParams
  ) => Promise<CompletedServiceItem>;

  createRegistration: (
    token: string,
    createRegistrationParams: CreateRegistrationParams
  ) => Promise<CreateRegistrationResponse>;
}

const namespace = '/oram';

const oramHeaders = (token: string) => ({
  headers: new Headers({
    'content-type': 'application/json',
    Authorization: `Bearer ${token}`,
  }),
});

const fetchTrackedVehicle = async (
  fetchTrackedVehicleParams: FetchTrackedVehicleParams,
  token: string,
  queryKey: string
) => {
  if (fetchTrackedVehicleParams.state === 'All') {
    delete fetchTrackedVehicleParams.state;
  }

  const init = {
    ...oramHeaders(token),
    method: 'GET',
  };

  let response = await request(
    constructURL(`${namespace}/tracked_vehicles/search`, {
      ...fetchTrackedVehicleParams,
    }),
    init
  );

  if (response?.status === 404) {
    const init = {
      ...oramHeaders(token),
      method: 'POST',
      body: JSON.stringify(fetchTrackedVehicleParams),
    };
    response = (await request(
      `${namespace}/tracked_vehicles`,
      init
    )) as RawTrackedVehicle;
  }
  return parseTrackedVehicleResponse(
    response,
    queryKey
  ) as ParsedTrackedVehicle;
};

const createTrackedVehicle = async (
  token: string,
  params: CreateTrackedVehicleParams
) => {
  const init = {
    ...oramHeaders(token),
    method: 'POST',
    body: JSON.stringify(params),
  };

  return await request(`${namespace}/tracked_vehicles`, init);
};

// Adds YMM to if not created with YMM, or can be used to set currently_tracked to false or true
const updateTrackedVehicle = async (
  token: string,
  updateTrackedVehicleParams: UpdateTrackedVehicleParams
) => {
  const init = {
    ...oramHeaders(token),
    method: 'PUT',
    body: JSON.stringify(updateTrackedVehicleParams),
  };

  return await request(`${namespace}/tracked_vehicles/update_status`, init);
};

const updateVehicleDetail = async (
  token: string,
  vehiclePermalink: string,
  newFormData: UpdateTrackedVehicleDetailParams,
  currentOramData: UpdateTrackedVehicleDetailParams
) => {
  const requestInit: RequestInitProps = {
    ...oramHeaders(token),
    method: 'POST',
    body: {
      source: newFormData.source,
      tracked_vehicle_id: newFormData.tracked_vehicle_id,
    },
  };

  const promises = [];

  if (newFormData.average_mileage !== currentOramData.average_mileage) {
    const averageMileagePromise = updateAverageMileage(
      newFormData,
      requestInit
    );
    promises.push(averageMileagePromise);
  }

  if (newFormData.mileage !== currentOramData.mileage) {
    const currentMileagePromise = updateCurrentMileage(
      newFormData,
      requestInit
    );
    promises.push(currentMileagePromise);
  }

  if (
    newFormData.city !== currentOramData.city ||
    newFormData.state !== currentOramData.state ||
    newFormData.zip !== currentOramData.zip
  ) {
    const locationPromise = updateLocation(newFormData, requestInit);
    promises.push(locationPromise);
  }

  await Promise.all(promises);
  return { vehiclePermalink };
};

const updateCurrentMileage = async (
  updateCurrentMileageParams: UpdateTrackedVehicleDetailParams,
  requestInit: RequestInitProps
) => {
  const mileageInit = {
    ...requestInit,
    body: JSON.stringify({
      ...requestInit.body,
      mileage: updateCurrentMileageParams.mileage,
    }),
  };

  return await request(`${namespace}/mileage_reports`, mileageInit);
};

const updateAverageMileage = async (
  updateAverageMileageParams: UpdateTrackedVehicleDetailParams,
  requestInit: RequestInitProps
) => {
  const averageMileageInit = {
    ...requestInit,
    body: JSON.stringify({
      ...requestInit.body,
      average_mileage: updateAverageMileageParams.average_mileage,
    }),
  };

  return await request(
    `${namespace}/average_driven_mileages`,
    averageMileageInit
  );
};

const updateLocation = async (
  updateLocationParams: UpdateTrackedVehicleDetailParams,
  requestInit: RequestInitProps
) => {
  const locationInit = {
    ...requestInit,
    body: JSON.stringify({
      ...requestInit.body,
      city: updateLocationParams.city,
      state: updateLocationParams.state,
      zip: updateLocationParams.zip,
    }),
  };

  return await request(`${namespace}/locations`, locationInit);
};

const createServiceItem = async (
  token: string,
  createParams: CreateServiceItemParams
) => {
  const init = {
    ...oramHeaders(token),
    method: 'POST',
    body: JSON.stringify(createParams),
  };

  return await request(`${namespace}/service_items`, init);
};

const completeServiceItem = async (
  token: string,
  completeServiceItemParams: CompleteServiceItemParams
) => {
  const init = {
    headers: new Headers({
      'content-type': 'application/json',
      Authorization: `Bearer ${token}`,
    }),
    method: 'PUT',
    body: JSON.stringify({
      completed: true,
      completed_mileage: completeServiceItemParams.mileage,
      source: 'user',
    }),
  };

  const response = await request(
    `${namespace}/service_items/${completeServiceItemParams.id}`,
    init
  );

  return {
    ...response,
    tracked_vehicle_id: completeServiceItemParams.tracked_vehicle_id,
  };
};

const createRegistration = async (
  token: string,
  createRegistrationParams: CreateRegistrationParams
) => {
  const init = {
    ...oramHeaders(token),
    method: 'POST',
    body: JSON.stringify(createRegistrationParams),
  };
  return await request(`${namespace}/registrations`, init);
};

export const OramRequests: OramRequestsInterface = {
  fetchTrackedVehicle,
  createTrackedVehicle,
  updateTrackedVehicle,
  updateVehicleDetail,
  createServiceItem,
  completeServiceItem,
  createRegistration,
};
